import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { showPaymentAlert } from './auth/helpers/auth-helper';
import { LandinPage } from './auth/models/IUser';
import { AuthService, LogoutEventPayload } from './auth/services/auth.service';
import { AlertComponent } from './core/components/alert/alert.component';
import { LoaderComponent } from './core/components/loader/loader.component';
import { StorageService } from './core/helpers/storage.service';
import { Storagekeys } from './core/model/storage';
import { AlertService } from './core/services/alert.service';
import { LogLevel, LoggerService } from './core/services/logger.service';
import { AgentService } from './web-agent/services/agent-service';
import { USMService } from './web-agent/services/usm-service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [RouterOutlet, AlertComponent, LoaderComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'kunnect-host-portal';
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly storage: StorageService,
    private readonly agentService: AgentService,
    private readonly usmService: USMService,
    private readonly logger: LoggerService,
    private readonly alertService: AlertService
  ) {}
  private getSessionSocketUrl(): string {
    if ((environment as { SESSION_SOCKET_URL?: string }).SESSION_SOCKET_URL) {
      return (environment as { SESSION_SOCKET_URL?: string })
        .SESSION_SOCKET_URL as string;
    }
    return environment.API_URL.replace(/^http/i, 'ws').replace(/\/api\/?$/, '');
  }

  private setupSessionSocket() {
    const sessionId = this.storage.getItem(Storagekeys.sessionId, true);
    const email = this.storage.getItem(Storagekeys.email, true);
    if (!sessionId || !email) {
      return;
    }
    this.authService.initSessionSocket(
      this.getSessionSocketUrl(),
      { sessionId, email },
      () => {
        this.initiateLogout('Verify Session Failed');
        this.storage.resetUserSession();
        this.router.navigate(['/auth/login'], {
          queryParams: { forceLogout: true },
        });
      }
    );
  }

  ngOnInit() {
    this.authService?.cleanUpSubscriptions();
    this.authService?.cleanAuthUpSubscriptions();
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
    const logoutSubscription = this.authService?.logoutEvent?.subscribe(msg => {
      const logoutPayload = this.normalizeLogoutPayload(msg);
      this.initiateLogout(logoutPayload.message);
      const token = this.storage?.getItem(Storagekeys.token, true);
      if (token) {
        this.authService.logout().subscribe({
          next: () => {
            console.log(`Logout Auth API Success :=> on ${new Date()}`);
            this.storage?.resetUserSession();
            this.router.navigate(['auth/login'], {
              queryParams: logoutPayload.forceLogout
                ? { forceLogout: true }
                : {},
            });
          },
          error: () => {
            this.storage.resetUserSession();
            this.router.navigate(['auth/login'], {
              queryParams: logoutPayload.forceLogout
                ? { forceLogout: true }
                : {},
            });
          },
        });
      } else {
        this.storage.resetUserSession();
        this.router.navigate(['auth/login'], {
          queryParams: logoutPayload.forceLogout ? { forceLogout: true } : {},
        });
      }
    });
    this.authService.subscriptions?.push(logoutSubscription);
    this.setLoginListner();
    const token = this.storage?.getItem(Storagekeys.token, true);
    const reDirectTo = this.storage.getItem(Storagekeys.redirectTo, true);
    if (token) {
      if (
        reDirectTo !== LandinPage.HOST_PAGE &&
        reDirectTo !== LandinPage.VERIFY_PAGE
      ) {
        this.authService.getAllUserPermissions().subscribe();
        if (environment.ENV !== 'qa' && environment.ENV !== 'performance') {
          showPaymentAlert(this.alertService, this.authService, this.storage);
        } else this.authService?.isExpireEvent?.emit(false);
      }
      if (
        this.authService.isAuthenticatedUser() &&
        !this.router.url.includes('login')
      ) {
        this.startSessionCheck();
      }
    }
  }
  initiateLogout(msg: string) {
    this.logger.log(LogLevel.INFO, `Logout Cause :=> `, msg);
    console.log(`Logout Cause :=> ${msg} on ${new Date()}`);
    document.body.classList.remove('payment-required');
    this.agentService.postCallEvent?.emit();
    this.agentService.logOff();
    this.usmService.logOff('App Comp');
    this.usmService.reset();
    this.agentService.reset();
    this.authService.cleanUpSubscriptions();
    this.authService.clearPermissionsCache();
  }

  private normalizeLogoutPayload(
    payload: string | LogoutEventPayload
  ): LogoutEventPayload {
    if (typeof payload === 'string') {
      return { message: payload };
    }
    return payload;
  }
  startSessionCheck() {
    this.setupSessionSocket();
  }
  setLoginListner() {
    const loginSubscription = this.authService.loginEvent?.subscribe({
      next: (res: boolean) => {
        if (res) {
          this.startSessionCheck();
          this.agentService.setLoginState();
        }
      },
    });
    this.authService.subscriptions?.push(loginSubscription);
  }

  ngOnDestroy(): void {
    console.log('App On Destroy...');
    this.authService?.cleanAuthUpSubscriptions();
    this.authService?.cleanUpSubscriptions();
  }
}
